import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { CheckCircle as CheckInIcon } from '@mui/icons-material';
import type { Appointment } from '@app-types/reception';

interface CheckinModalProps {
  open: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onConfirm: (appointmentId: string) => Promise<void>;
}

export const CheckinModal: React.FC<CheckinModalProps> = ({
  open,
  appointment,
  onClose,
  onConfirm
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!appointment) return;

    setLoading(true);
    setError(null);

    try {
      await onConfirm(appointment.id);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Eroare la check-in');
    } finally {
      setLoading(false);
    }
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CheckInIcon color="success" />
          Check-in pacient
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          Confirmati sosirea pacientului:
        </Typography>

        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mt: 2 }}>
          <Typography variant="body2">
            <strong>Animal:</strong> {appointment.animal?.name || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Proprietar:</strong>{' '}
            {appointment.owner
              ? `${appointment.owner.firstName} ${appointment.owner.lastName}`
              : 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Medic:</strong>{' '}
            {appointment.doctor
              ? `Dr. ${appointment.doctor.firstName} ${appointment.doctor.lastName}`
              : 'N/A'}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          Medicul va fi notificat automat ca pacientul a sosit.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Anuleaza
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckInIcon />}
        >
          {loading ? 'Se proceseaza...' : 'Confirma check-in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

